import { useCallback, useEffect, useState } from 'react'

import { useViewerContext } from 'Pods/ThreeDStudio/Shared/ViewerContext/ViewerContext'

import { EMPTY_ARRAY } from 'Utils/stableDefaults'

// Mirrors the viewer's DEFAULT_ANIMATION_SETTINGS for the window before a model
// is bound, and for the tests that mount without a viewer instance.
const NO_ANIMATION = {
  clips: EMPTY_ARRAY,
  activeClipId: null,
  playing: false,
  currentTime: 0,
  duration: 0,
}

/**
 * Reads the viewer's animation store and exposes the player's controls.
 * `currentTime` is published per frame by the viewer while a clip advances, so
 * the progress line repaints without a ticker of its own.
 *
 * @returns {{
 *   hasClips: boolean,
 *   isPlaying: boolean,
 *   currentTime: number,
 *   duration: number,
 *   togglePlay: function,
 *   seek: function,
 * }}
 */
export function useAnimationPlayback() {
  const viewer = useViewerContext()
  const [settings, setSettings] = useState(NO_ANIMATION)

  useEffect(() => {
    if (!viewer) {
      return undefined
    }

    // Seeded before subscribing: a model bound before this mounted has already
    // published its clips, and the store only notifies on change.
    setSettings(viewer.getAnimationSettings())

    return viewer.subscribe('animation', setSettings)
  }, [viewer])

  const { clips, activeClipId, playing, currentTime, duration } = settings

  // The viewer binds clips paused. The bar exists to show the animation, so a
  // freshly bound clip starts on its own; gating on activeClipId keeps that from
  // overriding a manual pause, which leaves the clip selected.
  useEffect(() => {
    if (viewer && clips.length > 0 && activeClipId == null) {
      viewer.playAnimation()
    }
  }, [viewer, clips, activeClipId])

  const togglePlay = useCallback(() => {
    if (!viewer) {
      return
    }

    if (viewer.getAnimationSettings().playing) {
      viewer.pauseAnimation()

      return
    }

    viewer.playAnimation()
  }, [viewer])

  const seek = useCallback((seconds) => {
    viewer?.seekAnimation(seconds)
  }, [viewer])

  return {
    hasClips: clips.length > 0,
    isPlaying: playing,
    currentTime,
    duration,
    togglePlay,
    seek,
  }
}
